import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import SectionHeader from "@/components/SectionHeader";
import { UsersFour, Users, Calendar, ClipboardText, Certificate, Warning, PlusCircle, Trash, PencilSimple, MagnifyingGlass, Phone, EnvelopeSimple, TreeStructure, Briefcase, DownloadSimple, UploadSimple, Receipt, FilePdf, CaretDown, UsersThree, Globe, HourglassMedium, ClockCounterClockwise, QrCode, Wrench, X, Plus, ArrowsCounterClockwise, ChatCircleDots, WhatsappLogo } from "@phosphor-icons/react";
import { api, apiDelete, apiError } from "@/lib/api";
import { useConfirmDelete } from "@/components/ConfirmDeleteDialog";
import { _resetEmployeeDirectoryCache } from "@/components/EmployeeAutocomplete";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import OrgChartTab from "@/components/hr/OrgChartTab";
import VacanciesTab from "@/components/hr/VacanciesTab";
import TalentPoolTab from "@/components/hr/TalentPoolTab";
import CalendarTab from "@/components/hr/CalendarTab";
import ReimbursementsTab from "@/components/hr/ReimbursementsTab";
import KpisTab from "@/components/hr/KpisTab";
import SignaturesLogTab from "@/components/hr/SignaturesLogTab";
import WhatsAppLogTab from "@/components/hr/WhatsAppLogTab";
import PuebleRosterEditor from "@/components/hr/PuebleRosterEditor";
import TwilioDiagnosticsPanel from "@/components/hr/TwilioDiagnosticsPanel";
import SignaturePad from "@/components/SignaturePad";
import SelfieCapture from "@/components/SelfieCapture";
import { Signature } from "@phosphor-icons/react";
import CareersQrDialog from "@/components/hr/CareersQrDialog";
import HrRequestsTab from "@/components/hr/HrRequestsTab";
import MyHr from "@/pages/MyHr";
import { useOpsProjects } from "@/components/OpsProjectSelector";
import { CatalogCombobox } from "@/components/CatalogCombobox";

const CREWS = ["A", "B", "C", "N/A"];
const REGIMES = ["14x7", "10x4", "20x10", "5x2"];
const SHIFT_TYPES = { day: "Diurno", night: "Nocturno", rotating: "Rotativo" };
const ROLES_LABEL = {
  operativo: "Operativo", mantenimiento: "Mantenimiento", almacen: "Almacén",
  campamento: "Campamento", residente_mina: "Residente de mina",
  gerente_proyecto: "Gerente de proyecto", administrativo: "Administrativo", jefatura: "Jefatura",
};
const STATUS_LABEL = {
  active: "Activo", vacation: "Vacaciones", rest: "Descanso",
  leave: "Baja / permiso", terminated: "Cesado",
};
const ATT_STATUS = [
  { v: "present", l: "Presente", color: "bg-emerald-500" },
  { v: "absent", l: "Ausente", color: "bg-red-500" },
  { v: "justified", l: "Justificado", color: "bg-amber-500" },
  { v: "rest", l: "Descanso", color: "bg-slate-500" },
  { v: "vacation", l: "Vacaciones", color: "bg-blue-500" },
  { v: "permit", l: "Permiso", color: "bg-purple-500" },
  { v: "medical", l: "Médico", color: "bg-orange-500" },
];
const emptyEmp = () => ({
  dni: "", name: "", position: "", area: "", role: "operativo", crew: "A", regime: "14x7", project_id: "",
  shift_type: "day", shift_start_date: "", employer: "propia", contractor_name: "", hire_date: "", base_salary: "",
  reports_to: "", phone: "", whatsapp: "", email: "", blood_type: "",
  rfid_code: "", helmet_barcode: "",
  emergency_contact: { name: "", relationship: "", phone: "" },
  medical: { blood_type: "", exam_date: "", exam_expires: "" },
  inductions: [], certifications: [], status: "active",
  photo_url: "",
});

/* ============ Crews manager ============ */
function CrewsManager({ crews, onChanged }) {
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({});
  const [name, setName] = useState("");

  useEffect(() => {
    if (open) api.get("/hr/crews").then((r) => setCounts(r.data.counts || {})).catch(() => {});
  }, [open]);

  const add = async () => {
    const n = name.trim().toUpperCase();
    if (!n) return;
    try {
      await api.post("/hr/crews", { name: n });
      toast.success(`Cuadrilla ${n} creada`);
      setName("");
      const r = await api.get("/hr/crews");
      setCounts(r.data.counts || {});
      onChanged(r.data.crews);
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/hr/crews/${encodeURIComponent(c)}`);
      const r = await api.get("/hr/crews");
      setCounts(r.data.counts || {});
      onChanged(r.data.crews);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="crews-manage-btn">
          <UsersThree size={14} weight="bold" /> Cuadrillas
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-sm">
        <DialogHeader><DialogTitle className="font-heading">Gestión de cuadrillas</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre (ej. D, NOCHE-1)"
                 onKeyDown={(e) => e.key === "Enter" && add()} data-testid="crews-new-name" />
          <Button onClick={add} className="bg-blue-900 hover:bg-blue-800 shrink-0" data-testid="crews-add-btn">
            <PlusCircle size={14} weight="bold" />
          </Button>
        </div>
        <div className="space-y-1 mt-2" data-testid="crews-list">
          {crews.map((c) => (
            <div key={c} className="flex items-center justify-between bg-slate-50 rounded px-3 py-1.5 text-sm">
              <span className="font-semibold">{c} <span className="text-[10px] text-slate-400 font-normal">· {counts[c] ?? 0} colaboradores</span></span>
              {!CREWS.includes(c) && (
                <button onClick={() => remove(c)} className="text-slate-400 hover:text-red-500" data-testid={`crews-delete-${c}`}>
                  <Trash size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
        <p className="text-[10px] text-slate-400">Las cuadrillas base (A, B, C, N/A) no se pueden eliminar. Las personalizadas solo si no tienen colaboradores.</p>
      </DialogContent>
    </Dialog>
  );
}

/* ============ Employees ============ */
function PromoteAdHocButton({ emp, onDone }) {
  const [open, setOpen] = useState(false);
  const [dni, setDni] = useState(emp.dni || "");
  const [hireDate, setHireDate] = useState(new Date().toISOString().slice(0, 10));
  const [phone, setPhone] = useState(emp.phone || "");
  const [email, setEmail] = useState(emp.email || "");
  const [salary, setSalary] = useState(emp.base_salary || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!dni.trim() || !hireDate) { toast.error("DNI y fecha de contratacion requeridos"); return; }
    setBusy(true);
    try {
      const payload = { dni: dni.trim(), hire_date: hireDate };
      if (phone.trim()) payload.phone = phone.trim();
      if (email.trim()) payload.email = email.trim();
      if (salary) payload.base_salary = Number(salary);
      await api.post(`/hr/employees/${emp.id}/promote`, payload);
      toast.success(`${emp.name} promovido(a) a alta formal`);
      _resetEmployeeDirectoryCache();
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`hr-emp-promote-${emp.id}`}
          title="Promover a alta formal en Nomina (completar DNI y contrato)"
          className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-amber-500 text-white hover:bg-amber-600 mr-2"
        >Promover</button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md" data-testid={`hr-emp-promote-dialog-${emp.id}`}>
        <DialogHeader><DialogTitle className="font-heading">Promover a alta formal</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">
          Completar los datos de <b>{emp.name}</b> para dejarla como alta formal en Nomina.
          Los campos que ya tenia (puesto, cuadrilla, regimen) se conservan.
        </p>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="overline-label">DNI / CURP *</Label>
            <Input value={dni} onChange={(e) => setDni(e.target.value)} data-testid={`hr-emp-promote-dni-${emp.id}`} />
          </div>
          <div>
            <Label className="overline-label">Fecha ingreso *</Label>
            <Input type="date" value={hireDate} onChange={(e) => setHireDate(e.target.value)} data-testid={`hr-emp-promote-hire-${emp.id}`} />
          </div>
          <div>
            <Label className="overline-label">Telefono</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <Label className="overline-label">Correo</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="overline-label">Salario base (opcional)</Label>
            <Input type="number" min={0} step={0.01} value={salary} onChange={(e) => setSalary(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={save} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid={`hr-emp-promote-save-${emp.id}`}>
            {busy ? "Promoviendo..." : "Promover a alta formal"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function EmployeesTab({ canManage }) {
  const [items, setItems] = useState([]);
  const opsProjects = useOpsProjects();
  const projectName = (id) => { const p = opsProjects.find((x) => x.id === id); return p ? (p.code ? `${p.code} · ${p.name}` : p.name) : "Proyecto"; };
  const [allEmployees, setAllEmployees] = useState([]);   // Full list for "Reporta a" dropdown (unfiltered)
  const [q, setQ] = useState("");
  const [filterCrew, setFilterCrew] = useState("all");
  const [sortBy, setSortBy] = useState("name-asc");   // iter98: orden configurable
  const [crews, setCrews] = useState(CREWS);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyEmp());
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/hr/crews").then((r) => setCrews(r.data.crews)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (filterCrew !== "all") params.set("crew", filterCrew);
    api.get(`/hr/employees?${params.toString()}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [q, filterCrew]);
  useEffect(() => { load(); }, [load]);

  // Iter98: ordenamiento cliente — trabaja sobre `items` para no re-fetchear.
  const sortedItems = useMemo(() => {
    const arr = [...items];
    const cmp = (a, b) => (a || "").localeCompare(b || "", "es", { sensitivity: "base" });
    switch (sortBy) {
      case "name-desc": arr.sort((a, b) => cmp(b.name, a.name)); break;
      case "crew": arr.sort((a, b) => cmp(a.crew, b.crew) || cmp(a.name, b.name)); break;
      case "position": arr.sort((a, b) => cmp(a.position, b.position) || cmp(a.name, b.name)); break;
      case "role": arr.sort((a, b) => cmp(a.role, b.role) || cmp(a.name, b.name)); break;
      case "dni": arr.sort((a, b) => cmp(a.dni, b.dni)); break;
      case "hire-desc": arr.sort((a, b) => cmp(b.hire_date, a.hire_date)); break;
      case "hire-asc": arr.sort((a, b) => cmp(a.hire_date, b.hire_date)); break;
      case "salary-desc": arr.sort((a, b) => (b.base_salary || 0) - (a.base_salary || 0)); break;
      case "name-asc":
      default: arr.sort((a, b) => cmp(a.name, b.name));
    }
    return arr;
  }, [items, sortBy]);

  // Independent unfiltered fetch for the "Reporta a" dropdown so it always
  // shows every active employee regardless of the search/crew filters above.
  const reloadAll = useCallback(() => {
    api.get("/hr/employees").then((r) => setAllEmployees(r.data)).catch(() => setAllEmployees([]));
  }, []);
  useEffect(() => { reloadAll(); }, [reloadAll]);

  const save = async () => {
    try {
      const payload = { ...form, base_salary: Number(form.base_salary || 0) };
      if (editing) await api.put(`/hr/employees/${editing}`, payload);
      else await api.post("/hr/employees", payload);
      toast.success("Colaborador guardado");
      _resetEmployeeDirectoryCache();
      setOpen(false); setEditing(null); setForm(emptyEmp()); load(); reloadAll();
    } catch (e) { toast.error(apiError(e)); }
  };
  const confirmDelete = useConfirmDelete();
  const remove = async (id, emp = null) => {
    const ok = await confirmDelete({
      title: "¿Eliminar colaborador?",
      itemLabel: emp ? `${emp.name} · ${emp.dni || ""} · ${emp.position || ""}` : "",
      description: "Se borrará su histórico de asistencia y quedará huella en auditoría minera. No se puede deshacer.",
    });
    if (!ok) return;
    try { await apiDelete(`/hr/employees/${id}`, ok.reason); _resetEmployeeDirectoryCache(); load(); reloadAll(); } catch (e) { toast.error(apiError(e)); }
  };
  const editOpen = (e) => { setEditing(e.id); setForm({ ...emptyEmp(), ...e }); setOpen(true); };
  const addCert = () => setForm((p) => ({ ...p, certifications: [...p.certifications, { name: "", issuer: "", issue_date: "", expires_date: "" }] }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <MagnifyingGlass size={18} className="text-slate-400" />
          <Input data-testid="hr-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nombre, DNI o cargo…" className="max-w-xs h-9" />
        </div>
        <Select value={filterCrew} onValueChange={setFilterCrew}>
          <SelectTrigger className="w-32 h-9" data-testid="hr-filter-crew"><SelectValue placeholder="Cuadrilla" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Todas</SelectItem>
            {crews.map((c) => <SelectItem key={c} value={c}>Cuadrilla {c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-44 h-9" data-testid="hr-sort-by">
            <SelectValue placeholder="Ordenar" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="name-asc">A → Z (nombre)</SelectItem>
            <SelectItem value="name-desc">Z → A (nombre)</SelectItem>
            <SelectItem value="crew">Cuadrilla</SelectItem>
            <SelectItem value="position">Cargo</SelectItem>
            <SelectItem value="role">Rol</SelectItem>
            <SelectItem value="dni">DNI</SelectItem>
            <SelectItem value="hire-desc">Ingreso · más reciente</SelectItem>
            <SelectItem value="hire-asc">Ingreso · más antiguo</SelectItem>
            <SelectItem value="salary-desc">Sueldo · mayor</SelectItem>
          </SelectContent>
        </Select>
        {canManage && <CrewsManager crews={crews} onChanged={setCrews} />}
        {canManage && (
          <>
            <Button
              variant="outline"
              size="sm"
              data-testid="hr-download-template"
              className="gap-1"
              onClick={async () => {
                try {
                  const r = await api.get("/hr/employees/template", { responseType: "blob" });
                  const url = URL.createObjectURL(r.data);
                  const a = document.createElement("a");
                  a.href = url; a.download = "plantilla_nomina_ug_oomining.xlsx"; a.click();
                  URL.revokeObjectURL(url);
                } catch (e) { toast.error(apiError(e)); }
              }}
            ><DownloadSimple size={14} weight="bold" /> Plantilla</Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="hr-import-nomina"
              className="gap-1"
              onClick={() => fileRef.current?.click()}
            ><UploadSimple size={14} weight="bold" /> Importar</Button>
            <PrePayrollButton />
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              data-testid="hr-import-file"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                const fd = new FormData();
                fd.append("file", file);
                try {
                  const { data } = await api.post("/hr/employees/import", fd);
                  toast.success(`✓ ${data.created} creados · ${data.updated} actualizados`);
                  if (data.errors?.length) toast.error(data.errors.slice(0, 3).join(" · "));
                  _resetEmployeeDirectoryCache();
                  load();
                } catch (err) { toast.error(apiError(err)); }
              }}
            />
          </>
        )}
        {canManage && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyEmp()); } }}>
            <DialogTrigger asChild>
              <Button data-testid="hr-new-employee" className="bg-blue-900 hover:bg-blue-800 gap-2"><PlusCircle size={14} weight="bold" /> Nuevo colaborador</Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar" : "Nuevo"} colaborador</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="overline-label">DNI</Label><Input data-testid="emp-dni" value={form.dni} onChange={(e) => setForm({ ...form, dni: e.target.value })} /></div>
                <div><Label className="overline-label">Nombre completo</Label><Input data-testid="emp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label className="overline-label">Cargo</Label>
                  <CatalogCombobox kind="cargo" value={form.position} onChange={(v) => setForm({ ...form, position: v })} placeholder="Ej. Perforista" canManage={canManage} testid="emp-position" /></div>
                <div><Label className="overline-label">Área</Label>
                  <CatalogCombobox kind="area" value={form.area} onChange={(v) => setForm({ ...form, area: v })} placeholder="Ej. Perforación" canManage={canManage} testid="emp-area" /></div>
                <div><Label className="overline-label">Rol</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="emp-role"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{Object.entries(ROLES_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="overline-label">Cuadrilla</Label>
                  <Select value={form.crew} onValueChange={(v) => setForm({ ...form, crew: v })}>
                    <SelectTrigger data-testid="emp-crew"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{crews.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select></div>
                {opsProjects.length > 0 && (
                  <div><Label className="overline-label">Proyecto asignado</Label>
                    <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
                      <SelectTrigger data-testid="emp-project"><SelectValue placeholder="Sin proyecto" /></SelectTrigger>
                      <SelectContent className="bg-white max-h-64">
                        <SelectItem value="__none__">Sin proyecto</SelectItem>
                        {opsProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code ? `${p.code} · ` : ""}{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                )}
                <div><Label className="overline-label">Régimen</Label>
                  <Select value={form.regime} onValueChange={(v) => setForm({ ...form, regime: v })}>
                    <SelectTrigger data-testid="emp-regime"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{REGIMES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="overline-label">Turno</Label>
                  <Select value={form.shift_type} onValueChange={(v) => setForm({ ...form, shift_type: v })}>
                    <SelectTrigger data-testid="emp-shift-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{Object.entries(SHIFT_TYPES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="overline-label">Inicio de ciclo</Label>
                  <Input data-testid="emp-shift-start" type="date" value={form.shift_start_date} onChange={(e) => setForm({ ...form, shift_start_date: e.target.value })} /></div>
                <div><Label className="overline-label">Reporta a</Label>
                  <Select value={form.reports_to || "__none__"} onValueChange={(v) => setForm({ ...form, reports_to: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="emp-reports-to"><SelectValue placeholder="Sin jefe directo" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      <SelectItem value="__none__">Sin jefe directo</SelectItem>
                      {allEmployees.filter((x) => x.id !== editing).map((x) => (
                        <SelectItem key={x.id} value={x.id}>{x.name} — {x.position || x.role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select></div>
                <div><Label className="overline-label">Empresa</Label>
                  <Select value={form.employer} onValueChange={(v) => setForm({ ...form, employer: v })}>
                    <SelectTrigger data-testid="emp-employer"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="propia">Propia</SelectItem>
                      <SelectItem value="contratista">Contratista</SelectItem>
                    </SelectContent>
                  </Select></div>
                {form.employer === "contratista" && (
                  <div><Label className="overline-label">Contratista</Label><Input data-testid="emp-contractor" value={form.contractor_name} onChange={(e) => setForm({ ...form, contractor_name: e.target.value })} /></div>
                )}
                <div><Label className="overline-label">Ingreso</Label><Input data-testid="emp-hire-date" type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} /></div>
                <div><Label className="overline-label">Sueldo base mensual ($)</Label><Input data-testid="emp-base-salary" type="number" step="any" min="0" placeholder="0" value={form.base_salary} onChange={(e) => setForm({ ...form, base_salary: e.target.value })} /></div>
                <div><Label className="overline-label">Teléfono</Label><Input data-testid="emp-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div>
                  <Label className="overline-label">WhatsApp (E.164)</Label>
                  <Input data-testid="emp-whatsapp" placeholder="+521234567890"
                         value={form.whatsapp}
                         onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
                  <p className="text-[10px] text-slate-500 italic mt-0.5">Deja vacío para usar el teléfono como fallback. Formato internacional con +52 (MX).</p>
                </div>
                <div><Label className="overline-label">Correo</Label><Input data-testid="emp-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label className="overline-label">Estado</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="emp-status"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{Object.entries(STATUS_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select></div>

                {/* Iter133 · Identificación física para el kiosko */}
                <div className="col-span-2 border-t pt-3">
                  <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Identificación física (kiosko)</p>
                  <p className="text-[10px] text-slate-500 italic mb-2">
                    Registra la tarjeta RFID/HID (número que emite el lector al pasarla) o el código de barras impreso en el casco.
                    En el modo Kiosco el operador solo pasa su tarjeta y aparece pre-seleccionado.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="emp-rfid-code" placeholder="Tarjeta RFID (ej. 0004892837)"
                            value={form.rfid_code}
                            onChange={(e) => setForm({ ...form, rfid_code: e.target.value.trim().toUpperCase() })} />
                    <Input data-testid="emp-helmet-barcode" placeholder="Código de barras del casco"
                            value={form.helmet_barcode}
                            onChange={(e) => setForm({ ...form, helmet_barcode: e.target.value.trim().toUpperCase() })} />
                  </div>
                </div>

                <div className="col-span-2 border-t pt-3 mt-1">
                  <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Contacto de emergencia</p>
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="Nombre" value={form.emergency_contact.name} onChange={(e) => setForm({ ...form, emergency_contact: { ...form.emergency_contact, name: e.target.value } })} />
                    <Input placeholder="Parentesco" value={form.emergency_contact.relationship} onChange={(e) => setForm({ ...form, emergency_contact: { ...form.emergency_contact, relationship: e.target.value } })} />
                    <Input placeholder="Teléfono" value={form.emergency_contact.phone} onChange={(e) => setForm({ ...form, emergency_contact: { ...form.emergency_contact, phone: e.target.value } })} />
                  </div>
                </div>

                <div className="col-span-2 border-t pt-3">
                  <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Foto de perfil (anti-suplantación)</p>
                  <p className="text-[10px] text-slate-500 italic mb-2">Se compara con el selfie del pase de turno. Si el % de coincidencia es bajo, se marca la asistencia como <b>sospechosa</b> y se avisa al supervisor.</p>
                  <div className="max-w-xs">
                    <SelfieCapture value={form.photo_url || ""} onChange={(url) => setForm({ ...form, photo_url: url })} testid="emp-photo" />
                  </div>
                </div>

                <div className="col-span-2 border-t pt-3">
                  <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Datos médicos</p>
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="Grupo sanguíneo" value={form.medical.blood_type} onChange={(e) => setForm({ ...form, medical: { ...form.medical, blood_type: e.target.value } })} data-testid="emp-blood-type" />
                    <Input type="date" placeholder="EMO" value={form.medical.exam_date} onChange={(e) => setForm({ ...form, medical: { ...form.medical, exam_date: e.target.value } })} />
                    <Input type="date" placeholder="EMO vence" value={form.medical.exam_expires} onChange={(e) => setForm({ ...form, medical: { ...form.medical, exam_expires: e.target.value } })} />
                  </div>
                </div>

                <div className="col-span-2 border-t pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Certificados / cursos</p>
                    <Button size="sm" variant="ghost" onClick={addCert} data-testid="emp-add-cert" className="h-7 text-xs text-blue-900"><PlusCircle size={12} weight="bold" className="mr-1" /> Agregar</Button>
                  </div>
                  {form.certifications.map((c, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 mb-2" data-testid={`emp-cert-row-${i}`}>
                      <Input className="col-span-4" placeholder="Nombre (ej. Altura)" value={c.name} onChange={(e) => setForm({ ...form, certifications: form.certifications.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} />
                      <Input className="col-span-3" placeholder="Emisor" value={c.issuer} onChange={(e) => setForm({ ...form, certifications: form.certifications.map((x, j) => j === i ? { ...x, issuer: e.target.value } : x) })} />
                      <Input className="col-span-2" type="date" value={c.issue_date} onChange={(e) => setForm({ ...form, certifications: form.certifications.map((x, j) => j === i ? { ...x, issue_date: e.target.value } : x) })} />
                      <Input className="col-span-2" type="date" value={c.expires_date} onChange={(e) => setForm({ ...form, certifications: form.certifications.map((x, j) => j === i ? { ...x, expires_date: e.target.value } : x) })} />
                      <button onClick={() => setForm({ ...form, certifications: form.certifications.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-500"><Trash size={14} /></button>
                    </div>
                  ))}
                  {!form.certifications.length && <p className="text-xs text-slate-400 italic">Sin certificados.</p>}
                </div>
              </div>
              <Button data-testid="emp-save" onClick={save} disabled={!form.dni || !form.name || !form.position} className="bg-blue-900 hover:bg-blue-800 w-full mt-2">
                {editing ? "Actualizar" : "Crear colaborador"}
              </Button>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 py-10 text-center">Sin colaboradores registrados</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="hr-emp-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider font-bold text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Nombre / DNI</th>
                  <th className="text-left px-3 py-2">Cargo · Área</th>
                  <th className="text-left px-3 py-2">Rol</th>
                  <th className="text-center px-3 py-2">Cuadrilla</th>
                  <th className="text-center px-3 py-2">Régimen</th>
                  <th className="text-left px-3 py-2">Contacto</th>
                  <th className="text-center px-3 py-2">Estado</th>
                  {canManage && <th></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedItems.map((e) => (
                  <tr key={e.id} data-testid={`hr-emp-row-${e.id}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <p className="font-semibold text-slate-900">
                        {e.name}
                        {e.created_via === "adhoc" && (
                          <span
                            data-testid={`hr-emp-adhoc-badge-${e.id}`}
                            title="Persona ad-hoc: falta DNI/contrato para alta formal. Click en 'Promover' para completar."
                            className="ml-2 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300"
                          >Ad-hoc</span>
                        )}
                      </p>
                      <p className="text-[11px] text-slate-400">DNI {e.dni || <span className="italic text-amber-600">sin DNI</span>}</p>
                    </td>
                    <td className="px-3 py-2">
                      <p className="text-slate-800">{e.position}</p>
                      <p className="text-[11px] text-slate-500">{e.area}</p>
                      {e.project_id && <p className="text-[10px] font-bold text-blue-800" data-testid={`emp-project-badge-${e.id}`}>{projectName(e.project_id)}</p>}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{ROLES_LABEL[e.role] || e.role}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="inline-block h-6 w-6 rounded-full bg-blue-100 text-blue-900 text-xs font-bold leading-6">{e.crew}</span>
                    </td>
                    <td className="px-3 py-2 text-center text-slate-700">{e.regime}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">
                      {e.phone && <span className="block"><Phone size={10} className="inline mr-1" />{e.phone}</span>}
                      {e.email && <span className="block truncate max-w-[160px]"><EnvelopeSimple size={10} className="inline mr-1" />{e.email}</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${e.status === "active" ? "bg-emerald-100 text-emerald-800" : e.status === "terminated" ? "bg-slate-200 text-slate-500" : "bg-amber-100 text-amber-800"}`}>
                        {STATUS_LABEL[e.status] || e.status}
                      </span>
                    </td>
                    {canManage && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {e.created_via === "adhoc" && (
                          <PromoteAdHocButton emp={e} onDone={() => { load(); reloadAll(); }} />
                        )}
                        <button onClick={() => editOpen(e)} data-testid={`hr-emp-edit-${e.id}`} className="text-slate-400 hover:text-blue-800 mr-2 ml-2"><PencilSimple size={14} /></button>
                        <button onClick={() => remove(e.id, e)} data-testid={`hr-emp-delete-${e.id}`} className="text-slate-400 hover:text-red-500"><Trash size={14} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============ Attendance ============ */
function PeriodReportsButton() {
  const [open, setOpen] = useState(false);
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const iso = (d) => d.toISOString().slice(0, 10);
  const [range, setRange] = useState({ from: iso(start), to: iso(today) });

  const download = async (endpoint, ext) => {
    try {
      const r = await api.get(endpoint, { params: range, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `asistencia-${range.from}-a-${range.to}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Descargado ${range.from} → ${range.to}`);
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="hr-att-period-btn"
                className="gap-1.5 border-blue-700 text-blue-800 hover:bg-blue-50">
          <FilePdf size={14} weight="bold" /> Reporte por período
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md" data-testid="hr-att-period-dialog">
        <DialogHeader><DialogTitle className="font-heading">Reporte de asistencia por período</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Consolida los conteos de presente / ausente / justificado / vacaciones /
            permiso / médico / descanso por colaborador en el rango de fechas.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline-label">Desde</Label>
              <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
                     className="h-9" data-testid="hr-att-period-from" /></div>
            <div><Label className="overline-label">Hasta</Label>
              <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
                     className="h-9" data-testid="hr-att-period-to" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button variant="outline" onClick={() => download("/hr/attendance/period-xlsx", "xlsx")}
                    data-testid="hr-att-period-xlsx" className="gap-1.5 border-emerald-700 text-emerald-800 hover:bg-emerald-50">
              <DownloadSimple size={14} weight="bold" /> Excel
            </Button>
            <Button onClick={() => download("/hr/attendance/period-pdf", "pdf")}
                    data-testid="hr-att-period-pdf" className="bg-blue-900 hover:bg-blue-800 gap-1.5">
              <FilePdf size={14} weight="bold" /> PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Iter115: editor de flota del Pueble — permite al admin fijar manualmente
// qué números económicos aparecen bajo cada grupo del PDF sin tocar código.
function PuebleFleetEditor() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [allCodes, setAllCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/hr/pueble/equipment-map");
      setRows((r.data?.groups || []).map((g) => ({ ...g, _new: "" })));
      setAllCodes(r.data?.all_codes || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const addCode = (idx, code) => {
    const c = (code || "").trim();
    if (!c) return;
    setRows((rs) => rs.map((r, i) => {
      if (i !== idx) return r;
      if ((r.equipment_codes || []).includes(c)) return { ...r, _new: "" };
      return { ...r, equipment_codes: [...(r.equipment_codes || []), c], has_override: true, _new: "" };
    }));
  };

  const removeCode = (idx, code) => {
    setRows((rs) => rs.map((r, i) => (i === idx
      ? { ...r, equipment_codes: (r.equipment_codes || []).filter((c) => c !== code), has_override: true }
      : r
    )));
  };

  const resetGroup = (idx) => {
    setRows((rs) => rs.map((r, i) => (i === idx
      ? { ...r, equipment_codes: r.auto_codes || [], has_override: false, _reset: true }
      : r
    )));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        groups: rows.map((r) => ({
          group: r.group,
          equipment_codes: r.has_override ? (r.equipment_codes || []) : [],
          reset_to_auto: !r.has_override && r._reset === true,
        })),
      };
      await api.put("/hr/pueble/equipment-map", payload);
      toast.success("Configuración de flota guardada");
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="hr-pueble-fleet-btn"
                className="gap-1.5 border-slate-400 text-slate-700 hover:bg-slate-50">
          <Wrench size={14} weight="bold" /> Flota Pueble
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-3xl" data-testid="hr-pueble-fleet-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Editor de flota del Pueble</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">
          Fija manualmente qué <b>números económicos</b> aparecen bajo cada grupo del PDF.
          Los grupos marcados como <span className="text-amber-800 font-bold">manual</span> ignoran el auto-detect;
          los <span className="text-slate-600">auto</span> muestran lo que detecta el catálogo por type / nombre.
        </p>
        {loading ? (
          <p className="text-sm text-slate-500 py-4 text-center">Cargando…</p>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
            {rows.map((r, idx) => (
              <div key={r.group} className="border border-slate-200 rounded-md p-2.5" data-testid={`hr-pueble-fleet-row-${idx}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div>
                    <p className="font-heading font-bold text-slate-900 text-sm">{r.group}</p>
                    <p className="text-[10px] text-slate-500">
                      {r.has_override
                        ? <span className="text-amber-800 font-bold">◉ manual</span>
                        : <span>◎ auto — detecta {(r.auto_codes || []).length} equipo(s)</span>}
                    </p>
                  </div>
                  {r.has_override && (
                    <button type="button" onClick={() => resetGroup(idx)}
                            className="text-[10px] text-blue-800 hover:underline flex items-center gap-1"
                            data-testid={`hr-pueble-fleet-reset-${idx}`}>
                      <ArrowsCounterClockwise size={11} /> Volver a auto
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1 mb-2 min-h-[26px]">
                  {(r.equipment_codes || []).length === 0 && (
                    <span className="text-[10px] italic text-slate-400">— sin códigos —</span>
                  )}
                  {(r.equipment_codes || []).map((c) => (
                    <span key={c}
                          className="inline-flex items-center gap-1 bg-slate-100 border border-slate-300 rounded-full pl-2 pr-1 py-0.5 text-[11px] font-mono"
                          data-testid={`hr-pueble-fleet-chip-${idx}-${c}`}>
                      {c}
                      <button type="button" onClick={() => removeCode(idx, c)}
                              className="rounded-full hover:bg-red-100 p-0.5"
                              title="Quitar">
                        <X size={9} weight="bold" className="text-red-700" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1">
                  <Select value={r._new || ""}
                          onValueChange={(v) => addCode(idx, v)}>
                    <SelectTrigger className="h-7 text-xs flex-1" data-testid={`hr-pueble-fleet-add-${idx}`}>
                      <SelectValue placeholder="+ Agregar equipo del catálogo…" />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-56 overflow-y-auto">
                      {allCodes
                        .filter((c) => !(r.equipment_codes || []).includes(c))
                        .map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      {allCodes.length === 0 && (
                        <SelectItem value="__none__" disabled>Sin equipos activos en Mantenimiento</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <Input placeholder="o escribe código" value={r._new || ""}
                         onChange={(e) => setRows((rs) => rs.map((row, i) => i === idx ? { ...row, _new: e.target.value } : row))}
                         onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCode(idx, r._new); } }}
                         className="h-7 text-xs w-40"
                         data-testid={`hr-pueble-fleet-input-${idx}`} />
                  <Button size="sm" variant="outline" className="h-7 px-2"
                          onClick={() => addCode(idx, r._new)}
                          data-testid={`hr-pueble-fleet-addbtn-${idx}`}>
                    <Plus size={12} weight="bold" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="hr-pueble-fleet-cancel">Cancelar</Button>
          <Button className="bg-blue-900 hover:bg-blue-800 text-white" onClick={save} disabled={saving}
                  data-testid="hr-pueble-fleet-save">
            {saving ? "Guardando…" : "Guardar configuración"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PueblePeriodButton() {
  // Iter118-fix: estas 4 declaraciones se borraron por accidente en Iter115
  // al insertar PuebleFleetEditor via search_replace, provocando
  // `ReferenceError: iso is not defined` al renderizar HR — el error
  // crasheaba toda la página y las tabs (incluida Asistencia) quedaban
  // en blanco. Restauradas.
  const [open, setOpen] = useState(false);
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const iso = (d) => d.toISOString().slice(0, 10);
  const [range, setRange] = useState({ from: iso(start), to: iso(today) });
  const [mode, setMode] = useState("period"); // 'period' | 'shift'
  const [shiftDate, setShiftDate] = useState(iso(today));
  const [shift, setShift] = useState("dia");

  // ---- Filtros del reporte por período ----
  const ALL_CATEGORIES = ["Operativos", "Mantenimiento", "Auxiliares", "Almacén", "Otros"];
  const [selectedCats, setSelectedCats] = useState([]); // vacío = todas
  const [availableCrews, setAvailableCrews] = useState([]);
  const [selectedCrews, setSelectedCrews] = useState([]); // vacío = todas

  // Al abrir en modo período, precargar cuadrillas disponibles del rango actual
  useEffect(() => {
    if (!open || mode !== "period") return;
    api.get("/hr/pueble/period", { params: { from: range.from, to: range.to } })
      .then((r) => setAvailableCrews(r.data.crews_all || r.data.crews || []))
      .catch(() => setAvailableCrews([]));
  }, [open, mode, range.from, range.to]);

  const toggleCat = (c) => setSelectedCats((prev) =>
    prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  const toggleCrew = (c) => setSelectedCrews((prev) =>
    prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);

  const download = async (endpoint, ext, params, filename) => {
    try {
      const r = await api.get(endpoint, { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success("Pueble descargado");
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  // Sufijo de nombre para reflejar filtros aplicados
  const filterSuffix = () => {
    const parts = [];
    if (selectedCats.length > 0) parts.push(`cat${selectedCats.length}`);
    if (selectedCrews.length > 0) parts.push(`cuad${selectedCrews.length}`);
    return parts.length ? `-${parts.join("-")}` : "";
  };

  const periodParams = () => {
    const p = { from: range.from, to: range.to };
    if (selectedCats.length > 0) p.categories = selectedCats.join(",");
    if (selectedCrews.length > 0) p.crews = selectedCrews.join(",");
    return p;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="hr-pueble-btn"
                className="gap-1.5 border-amber-700 text-amber-800 hover:bg-amber-50">
          <UsersFour size={14} weight="bold" /> Pueble
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md" data-testid="hr-pueble-dialog">
        <DialogHeader><DialogTitle className="font-heading">Reporte Pueble (roster de cuadrillas)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Reproduce el layout <b>Pueble Silverco</b>: matriz Grupo × Rol × Cuadrilla.
            En modo <b>Turno único</b> muestra los nombres asignados; en modo
            <b> Período</b> muestra el <b>conteo de personas</b> únicas por rol/cuadrilla.
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant={mode === "shift" ? "default" : "outline"}
                    onClick={() => setMode("shift")} data-testid="hr-pueble-mode-shift"
                    className={mode === "shift" ? "bg-blue-900 text-white" : ""}>
              Turno único
            </Button>
            <Button size="sm" variant={mode === "period" ? "default" : "outline"}
                    onClick={() => setMode("period")} data-testid="hr-pueble-mode-period"
                    className={mode === "period" ? "bg-blue-900 text-white" : ""}>
              Período (personas)
            </Button>
          </div>

          {mode === "shift" ? (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="overline-label">Fecha</Label>
                <Input type="date" value={shiftDate} onChange={(e) => setShiftDate(e.target.value)}
                       className="h-9" data-testid="hr-pueble-shift-date" /></div>
              <div><Label className="overline-label">Turno</Label>
                <Select value={shift} onValueChange={setShift}>
                  <SelectTrigger className="h-9" data-testid="hr-pueble-shift"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="dia">Día</SelectItem>
                    <SelectItem value="noche">Noche</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="overline-label">Desde</Label>
                  <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
                         className="h-9" data-testid="hr-pueble-period-from" /></div>
                <div><Label className="overline-label">Hasta</Label>
                  <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
                         className="h-9" data-testid="hr-pueble-period-to" /></div>
              </div>

              {/* Filtro por categoría */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="overline-label">Categorías</Label>
                  {selectedCats.length > 0 && (
                    <button type="button" onClick={() => setSelectedCats([])}
                            data-testid="hr-pueble-cat-clear"
                            className="text-[10px] text-blue-700 hover:underline">
                      Limpiar
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1" data-testid="hr-pueble-cat-filter">
                  {ALL_CATEGORIES.map((c) => {
                    const active = selectedCats.includes(c);
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => toggleCat(c)}
                        data-testid={`hr-pueble-cat-${c.toLowerCase().replace("é","e").replace("á","a")}`}
                        className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold transition ${
                          active
                            ? "bg-blue-900 text-white border-blue-900"
                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  {selectedCats.length === 0 ? "Sin filtro → se muestran las 5 categorías." : `${selectedCats.length} categoría(s) seleccionada(s)`}
                </p>
              </div>

              {/* Filtro por cuadrilla */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="overline-label">Cuadrillas</Label>
                  {selectedCrews.length > 0 && (
                    <button type="button" onClick={() => setSelectedCrews([])}
                            data-testid="hr-pueble-crew-clear"
                            className="text-[10px] text-blue-700 hover:underline">
                      Limpiar
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1" data-testid="hr-pueble-crew-filter">
                  {availableCrews.length === 0 && (
                    <span className="text-[11px] italic text-slate-400">Sin datos en el rango…</span>
                  )}
                  {availableCrews.map((c) => {
                    const active = selectedCrews.includes(c);
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => toggleCrew(c)}
                        data-testid={`hr-pueble-crew-${c}`}
                        className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold transition ${
                          active
                            ? "bg-emerald-700 text-white border-emerald-700"
                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        Cuadrilla {c}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  {selectedCrews.length === 0 ? "Sin filtro → todas las cuadrillas presentes." : `${selectedCrews.length} cuadrilla(s) seleccionada(s)`}
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            {mode === "shift" ? (
              <>
                <Button variant="outline" data-testid="hr-pueble-shift-xlsx"
                        onClick={() => download("/hr/pueble/xlsx", "xlsx",
                          { date: shiftDate, shift },
                          `pueble-${shiftDate}-${shift}.xlsx`)}
                        className="gap-1.5 border-emerald-700 text-emerald-800 hover:bg-emerald-50">
                  <DownloadSimple size={14} weight="bold" /> Excel
                </Button>
                <Button data-testid="hr-pueble-shift-pdf"
                        onClick={() => download("/hr/pueble/pdf", "pdf",
                          { date: shiftDate, shift },
                          `pueble-${shiftDate}-${shift}.pdf`)}
                        className="bg-blue-900 hover:bg-blue-800 gap-1.5">
                  <FilePdf size={14} weight="bold" /> PDF
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" data-testid="hr-pueble-period-xlsx"
                        onClick={() => download("/hr/pueble/period/xlsx", "xlsx",
                          periodParams(),
                          `pueble-periodo-${range.from}-a-${range.to}${filterSuffix()}.xlsx`)}
                        className="gap-1.5 border-emerald-700 text-emerald-800 hover:bg-emerald-50">
                  <DownloadSimple size={14} weight="bold" /> Excel
                </Button>
                <Button data-testid="hr-pueble-period-pdf"
                        onClick={() => download("/hr/pueble/period/pdf", "pdf",
                          periodParams(),
                          `pueble-periodo-${range.from}-a-${range.to}${filterSuffix()}.pdf`)}
                        className="bg-blue-900 hover:bg-blue-800 gap-1.5">
                  <FilePdf size={14} weight="bold" /> PDF
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============ Attendance form ============ */
function AdHocPersonButton({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [crew, setCrew] = useState("N/A");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const n = name.trim();
    if (!n) { toast.error("Nombre requerido"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/hr/employees/ad-hoc", {
        name: n, position: position.trim(), crew,
      });
      onCreated?.(data);
      setOpen(false); setName(""); setPosition(""); setCrew("N/A");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 border-blue-300 text-blue-900 hover:bg-blue-50" data-testid="hr-att-adhoc-btn">
          <PlusCircle size={14} weight="bold" /> Nueva persona en sitio
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md" data-testid="hr-att-adhoc-dialog">
        <DialogHeader><DialogTitle className="font-heading">Agregar persona al pase de turno</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Rapido para informar turno a turno quien estara en sitio. La persona queda
            listada en Nomina como <b>activa</b>; el area de RH luego puede completar
            DNI, salario y fechas de contrato.
          </p>
          <div>
            <Label className="overline-label">Nombre completo</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Juan Perez Sanchez"
              data-testid="hr-att-adhoc-name"
              onKeyDown={(e) => e.key === "Enter" && save()} />
          </div>
          <div>
            <Label className="overline-label">Puesto</Label>
            <Input value={position} onChange={(e) => setPosition(e.target.value)}
              placeholder="Ej: Ayudante barrenacion, Mecanico externo, Contratista"
              data-testid="hr-att-adhoc-position" />
          </div>
          <div>
            <Label className="overline-label">Cuadrilla</Label>
            <Select value={crew} onValueChange={setCrew}>
              <SelectTrigger className="h-9" data-testid="hr-att-adhoc-crew"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {CREWS.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-800" data-testid="hr-att-adhoc-save">
            {busy ? "Creando..." : "Agregar y marcar presente"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function AttendanceTab({ canManage }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [emps, setEmps] = useState([]);
  const [marks, setMarks] = useState({});
  const [lastByEmp, setLastByEmp] = useState({}); // { [emp_id]: {status, date} }
  const [busy, setBusy] = useState(false);
  const [signTarget, setSignTarget] = useState(null); // {id, name} of employee to sign
  const [signDraft, setSignDraft] = useState("");
  const [selfieDraft, setSelfieDraft] = useState("");

  const load = useCallback(async () => {
    const [e, a, last] = await Promise.all([
      api.get("/hr/employees?active=true").then((r) => r.data).catch(() => []),
      api.get(`/hr/attendance?date=${date}`).then((r) => r.data).catch(() => []),
      api.get("/hr/attendance/last").then((r) => r.data).catch(() => ({})),
    ]);
    setEmps(e);
    setLastByEmp(last || {});
    // Prefill: today's record if exists → else remember last known status per employee
    const m = {};
    e.forEach((emp) => {
      const today = a.find((x) => x.employee_id === emp.id);
      if (today) {
        m[emp.id] = {
          status: today.status,
          note: today.note || "",
          inherited: false,
          signature_data_url: today.signature_data_url || "",
        };
      } else if (last?.[emp.id]?.status) {
        m[emp.id] = { status: last[emp.id].status, note: "", inherited: true, signature_data_url: "" };
      } else {
        m[emp.id] = { status: "present", note: "", inherited: true, signature_data_url: "" };
      }
    });
    setMarks(m);
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const set = (id, status) => setMarks((p) => ({ ...p, [id]: { ...(p[id] || { note: "" }), status, inherited: false } }));

  const openSign = (emp) => {
    setSignTarget(emp);
    setSignDraft(marks[emp.id]?.signature_data_url || "");
    setSelfieDraft(marks[emp.id]?.selfie_data_url || "");
  };
  const confirmSign = () => {
    if (!signDraft) { toast.warning("Dibuja la firma antes de confirmar"); return; }
    setMarks((p) => ({ ...p, [signTarget.id]: { ...(p[signTarget.id] || { status: "present", note: "" }), signature_data_url: signDraft, selfie_data_url: selfieDraft, inherited: false } }));
    toast.success(`Firma capturada · ${signTarget.name.split(" ")[0]}${selfieDraft ? " (con selfie)" : ""}`);
    setSignTarget(null);
    setSignDraft("");
    setSelfieDraft("");
  };
  const clearSign = (id) => setMarks((p) => ({ ...p, [id]: { ...(p[id] || { status: "present", note: "" }), signature_data_url: "", selfie_data_url: "" } }));

  const save = async () => {
    setBusy(true);
    try {
      const entries = Object.entries(marks).map(([employee_id, v]) => ({
        employee_id,
        status: v.status,
        note: v.note || "",
        signature_data_url: v.signature_data_url || "",
        selfie_data_url: v.selfie_data_url || "",
      }));
      const { data } = await api.post("/hr/attendance", { date, entries });
      const signedCount = data?.signed ?? 0;
      toast.success(`Asistencia guardada (${entries.length} colaboradores${signedCount ? ` · ${signedCount} con firma` : ""})`);
      // Refresh so the inherited flags go away for saved rows
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="hr-attendance">
      <div className="flex flex-wrap items-center gap-3">
        <div><Label className="overline-label">Fecha</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-40" data-testid="hr-att-date" /></div>
        {canManage && (
          <Button onClick={save} disabled={busy || !Object.keys(marks).length} data-testid="hr-att-save" className="bg-blue-900 hover:bg-blue-800">
            {busy ? "Guardando…" : "Guardar asistencia"}
          </Button>
        )}
        {canManage && (
          <AdHocPersonButton onCreated={(emp) => {
            setEmps((prev) => [...prev, emp]);
            setMarks((prev) => ({ ...prev, [emp.id]: { status: "present", note: "", inherited: false, signature_data_url: "" } }));
            toast.success(`${emp.name} agregado al pase de turno`);
          }} />
        )}
        <Button
          variant="outline"
          size="sm"
          data-testid="hr-att-pass-pdf"
          className="gap-1.5 border-emerald-700 text-emerald-800 hover:bg-emerald-50"
          onClick={async () => {
            try {
              const r = await api.get(`/hr/attendance/pass-pdf?date=${date}`, { responseType: "blob" });
              const url = URL.createObjectURL(r.data);
              const a = document.createElement("a"); a.href = url;
              a.download = `pase-de-turno-${date}.pdf`; a.click();
              URL.revokeObjectURL(url);
            } catch (e) { toast.error(apiError(e)); }
          }}
        >
          <FilePdf size={14} weight="bold" /> Pase de turno PDF
        </Button>
        <Button
          variant="outline"
          size="sm"
          data-testid="hr-att-audit-monthly-pdf"
          className="gap-1.5 border-purple-700 text-purple-800 hover:bg-purple-50"
          onClick={async () => {
            const month = date.slice(0, 7);
            try {
              const r = await api.get(`/hr/attendance/audit-monthly-pdf?month=${month}`, { responseType: "blob" });
              const url = URL.createObjectURL(r.data);
              const a = document.createElement("a"); a.href = url;
              a.download = `auditoria-asistencias-${month}.pdf`; a.click();
              URL.revokeObjectURL(url);
              toast.success(`Auditoría ${month} descargada`);
            } catch (e) { toast.error(apiError(e)); }
          }}
          title="PDF combinado: todas las firmas + selfies del mes, con flag rostro-match. Respaldo legal."
        >
          <FilePdf size={14} weight="bold" /> Auditoría mensual
        </Button>
        <PeriodReportsButton />
        <PueblePeriodButton />
        <PuebleFleetEditor />
        <PuebleRosterEditor />
        <span className="text-[11px] text-slate-500 ml-2">
          <span className="inline-block h-2 w-2 rounded-full bg-slate-400 align-middle mr-1" /> Prellenado con el último estado — firma cada fila y guarda.
        </span>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider font-bold text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Colaborador</th>
              <th className="text-center px-3 py-2">Cuadr.</th>
              <th className="text-center px-3 py-2">Último</th>
              <th className="text-center px-3 py-2">Estado</th>
              <th className="text-center px-3 py-2">Firma</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {emps.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">Sin colaboradores activos</td></tr>}
            {emps.map((e) => {
              const cur = marks[e.id]?.status || "present";
              const inherited = !!marks[e.id]?.inherited;
              const last = lastByEmp[e.id];
              const sig = marks[e.id]?.signature_data_url || "";
              return (
                <tr key={e.id} data-testid={`hr-att-row-${e.id}`}>
                  <td className="px-3 py-2">
                    <p className="font-semibold text-slate-800">{e.name}</p>
                    <p className="text-[10px] text-slate-400">{e.position}</p>
                  </td>
                  <td className="text-center"><span className="inline-block h-6 w-6 rounded-full bg-blue-100 text-blue-900 text-xs font-bold leading-6">{e.crew}</span></td>
                  <td className="text-center text-[10px] text-slate-500" data-testid={`hr-att-last-${e.id}`}>
                    {last ? (
                      <span title={`Última marca: ${last.date}`}>
                        <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1 align-middle ${ATT_STATUS.find((x) => x.v === last.status)?.color || "bg-slate-300"}`} />
                        {ATT_STATUS.find((x) => x.v === last.status)?.l || last.status}
                        <span className="block text-[9px] text-slate-400">{last.date}</span>
                      </span>
                    ) : <span className="italic text-slate-300">Sin historial</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className={`flex flex-wrap gap-1 justify-center ${inherited ? "opacity-100" : ""}`}>
                      {ATT_STATUS.map((s) => (
                        <button
                          key={s.v}
                          disabled={!canManage}
                          data-testid={`hr-att-btn-${e.id}-${s.v}`}
                          onClick={() => set(e.id, s.v)}
                          className={`text-[10px] font-bold uppercase px-2 py-1 rounded text-white transition ${s.color} ${cur === s.v ? (inherited ? "ring-2 ring-slate-400 ring-dashed" : "ring-2 ring-blue-500 scale-105") : "opacity-40 hover:opacity-70"}`}
                          title={cur === s.v && inherited ? "Heredado del último día registrado — guarda para confirmar" : s.l}
                        >
                          {s.l}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {sig ? (
                      <div className="flex items-center gap-1 justify-center" data-testid={`hr-att-sig-preview-${e.id}`}>
                        <img src={sig} alt="firma" className="h-8 w-16 object-contain bg-white border border-emerald-200 rounded" />
                        {marks[e.id]?.selfie_data_url && (
                          <img src={marks[e.id].selfie_data_url} alt="selfie" className="h-8 w-8 object-cover rounded-full border border-purple-300" title="Selfie capturado" data-testid={`hr-att-selfie-thumb-${e.id}`} />
                        )}
                        {canManage && (
                          <>
                            <button onClick={() => openSign(e)} className="text-[10px] text-blue-700 hover:underline" data-testid={`hr-att-sig-edit-${e.id}`}>Editar</button>
                            <button onClick={() => clearSign(e.id)} className="text-[10px] text-red-600 hover:underline" data-testid={`hr-att-sig-clear-${e.id}`}>Borrar</button>
                          </>
                        )}
                      </div>
                    ) : (
                      canManage ? (
                        <div className="flex items-center gap-1 justify-center">
                          {e.photo_url ? (
                            <img src={e.photo_url} alt="perfil" className="h-8 w-8 object-cover rounded-full border border-slate-200"
                                 title="Foto de perfil registrada — se comparará con el selfie" />
                          ) : (
                            <span className="text-[9px] uppercase font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" title="Sin foto de perfil — no habrá comparación anti-suplantación">
                              sin foto
                            </span>
                          )}
                          <Button size="sm" variant="outline" onClick={() => openSign(e)} data-testid={`hr-att-sig-open-${e.id}`} className="h-7 gap-1 text-[11px] border-blue-300 text-blue-800 hover:bg-blue-50">
                            <Signature size={12} weight="bold" /> Firmar + Selfie
                          </Button>
                        </div>
                      ) : <span className="text-[10px] text-slate-300 italic">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Diálogo de firma */}
      <Dialog open={!!signTarget} onOpenChange={(o) => { if (!o) { setSignTarget(null); setSignDraft(""); setSelfieDraft(""); } }}>
        <DialogContent className="bg-white max-w-2xl" data-testid="hr-att-sign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Signature size={18} weight="bold" className="text-blue-900" /> Firma de asistencia
            </DialogTitle>
          </DialogHeader>
          {signTarget && (
            <div className="space-y-3">
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 text-xs">
                <p className="font-semibold text-slate-900">{signTarget.name}</p>
                <p className="text-slate-500">{signTarget.position} · Cuadrilla {signTarget.crew} · {date}</p>
              </div>
              <p className="text-[11px] text-slate-500 italic">
                La firma se estampa en el PDF del pase de turno y aparece en la Bitácora de firmas para auditoría.
                El selfie es <b className="text-purple-700">obligatorio</b> para comparar contra la foto de perfil y detectar suplantaciones.
                Si el % de coincidencia es bajo, la asistencia se marca como <b>sospechosa</b> automáticamente.
              </p>
              {signTarget && !signTarget.photo_url && (
                <div className="text-[11px] bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-amber-800">
                  ⚠ Este colaborador no tiene <b>foto de perfil</b> registrada. Ve a Recurso Humano → Nómina → editar y añade la foto para activar la comparación anti-suplantación.
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-3">
                <div className="border border-slate-300 rounded bg-white p-2">
                  <p className="text-[10px] uppercase font-bold text-slate-500 mb-1">Firma manuscrita</p>
                  <SignaturePad value={signDraft} onChange={setSignDraft} width={340} height={140} testid="hr-att-sign-pad" />
                </div>
                <div className="border border-slate-300 rounded bg-white p-2">
                  <p className="text-[10px] uppercase font-bold text-slate-500 mb-1">Selfie <span className="text-red-600">(obligatorio)</span> · antisuplantación</p>
                  <SelfieCapture value={selfieDraft} onChange={setSelfieDraft} testid="hr-att-selfie" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => { setSignDraft(""); setSelfieDraft(""); }} data-testid="hr-att-sign-clear">Limpiar todo</Button>
                <Button variant="outline" size="sm" onClick={() => { setSignTarget(null); setSignDraft(""); setSelfieDraft(""); }}>Cancelar</Button>
                <Button size="sm" onClick={confirmSign} disabled={!signDraft} data-testid="hr-att-sign-confirm" className="bg-blue-900 hover:bg-blue-800">Confirmar firma</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============ Certifications ============ */
function CertificationsTab() {
  const [days, setDays] = useState(90);
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/hr/certifications/expiring?days_ahead=${days}`).then((r) => setItems(r.data)).catch(() => setItems([])); }, [days]);
  return (
    <div className="space-y-4" data-testid="hr-certs">
      <div className="flex items-center gap-2">
        <Label className="overline-label">Ventana</Label>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-40 h-9" data-testid="hr-certs-window"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="30">30 días</SelectItem>
            <SelectItem value="60">60 días</SelectItem>
            <SelectItem value="90">90 días</SelectItem>
            <SelectItem value="180">180 días</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 py-10 text-center">Ningún certificado por vencer en la ventana seleccionada.</p>
        ) : (
          <table className="w-full text-sm" data-testid="hr-certs-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider font-bold text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Colaborador</th>
                <th className="text-left px-3 py-2">Certificado</th>
                <th className="text-left px-3 py-2">Emisor</th>
                <th className="text-center px-3 py-2">Vence</th>
                <th className="text-center px-3 py-2">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((it, i) => (
                <tr key={i} data-testid={`hr-cert-row-${i}`}>
                  <td className="px-3 py-2">
                    <p className="font-semibold text-slate-800">{it.employee_name}</p>
                    <p className="text-[10px] text-slate-400">{it.position} · Cuadr. {it.crew}</p>
                  </td>
                  <td className="px-3 py-2">{it.certification}</td>
                  <td className="px-3 py-2 text-slate-500">{it.issuer}</td>
                  <td className="px-3 py-2 text-center">{it.expires_date}</td>
                  <td className="px-3 py-2 text-center">
                    {it.expired ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-red-100 text-red-700"><Warning size={10} weight="fill" /> Vencido hace {Math.abs(it.days_left)}d</span>
                    ) : it.days_left <= 30 ? (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-amber-100 text-amber-700">Vence en {it.days_left}d</span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">{it.days_left}d restantes</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ============ Main ============ */
// Iter135 · Pestañas organizadas por categoría (visual grouping)
const TAB_GROUPS = [
  { cat: "Panel", tabs: [
    { v: "dashboard", l: "hrTabKpis", I: UsersFour },
    { v: "orgchart", l: "hrTabOrgchart", I: TreeStructure },
  ]},
  { cat: "Personal", tabs: [
    { v: "employees", l: "hrTabPayroll", I: Users },
    { v: "certs", l: "hrTabCerts", I: Certificate },
    { v: "talent", l: "hrTabTalent", I: UsersFour },
  ]},
  { cat: "Operación", tabs: [
    { v: "schedule", l: "hrTabSchedule", I: Calendar },
    { v: "attendance", l: "hrTabAttendance", I: ClipboardText },
    { v: "signatures", l: "hrTabSignatures", I: FilePdf },
  ]},
  { cat: "Gestión", tabs: [
    { v: "requests", l: "hrTabRequests", I: HourglassMedium },
    { v: "vacancies", l: "hrTabVacancies", I: Briefcase },
    { v: "reimb", l: "hrTabReimb", I: Receipt },
  ]},
  { cat: "Comunicación", tabs: [
    { v: "whatsapp", l: "hrTabWhatsApp", I: ChatCircleDots },
  ]},
];
const TABS = TAB_GROUPS.flatMap((g) => g.tabs);

const PDF_REPORTS = [
  { s: "general", l: "hrPdfGeneral" },
  { s: "nomina", l: "hrPdfPayroll" },
  { s: "calendar", l: "hrPdfCalendar" },
  { s: "certs", l: "hrPdfCerts" },
  { s: "vacancies", l: "hrPdfVacancies" },
  { s: "reimbursements", l: "hrPdfReimb" },
];

function HrPdfMenu() {
  const { t } = useLang();
  const download = async (section) => {
    try {
      toast.info(t("hrPdfGenerating"));
      const r = await api.get(`/hr/exports/${section}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `rrhh_${section}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="hr-pdf-menu-btn">
          <FilePdf size={14} weight="bold" /> {t("pdfReports")} <CaretDown size={11} weight="bold" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white">
        {PDF_REPORTS.map((r) => (
          <DropdownMenuItem key={r.s} onClick={() => download(r.s)} data-testid={`hr-pdf-${r.s}`}
                            className={r.s === "general" ? "font-bold text-blue-900" : ""}>
            {t(r.l)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function HR() {
  const { t } = useLang();
  const { user } = useAuth();
  const canManage = ["admin", "manager"].includes(user?.role);
  const [qrOpen, setQrOpen] = useState(false);

  // Iter134 · deep-link soporte: `/hr?tab=whatsapp` abre directamente la
  // pestaña de configuración Twilio (para que "Configurar Twilio" desde
  // Settings, un botón externo o un banner interno lleven al usuario justo
  // al panel de opt-in).
  const initialTab = (() => {
    try {
      return new URL(window.location.href).searchParams.get("tab") || "dashboard";
    } catch { return "dashboard"; }
  })();
  const [tab, setTab] = useState(initialTab);

  // Self-service: any non-admin sees ONLY their own record + can update
  // their attendance and submit permit/vacation requests. Admin/jefatura see
  // the full HR management view below.
  if (user && user.role !== "admin" && user.role !== "jefatura") {
    return <MyHr />;
  }

  return (
    <div className="space-y-6">
      <SectionHeader icon={UsersFour} title={t("hr")} eyebrow={t("hrSubtitle")} size="lg" testid="hr-header"
        right={
          <>
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="hr-careers-qr"
                    onClick={() => setQrOpen(true)}>
              <QrCode size={14} weight="bold" /> {t("hrQrPortal")}
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="hr-careers-link"
                    onClick={() => {
                      const url = `${window.location.origin}/careers`;
                      navigator.clipboard?.writeText(url);
                      toast.success(t("hrCareersLinkCopied") + ": " + url);
                      window.open(url, "_blank");
                    }}>
              <Globe size={14} weight="bold" /> {t("hrCareersPortal")}
            </Button>
            <HrPdfMenu />
            {(canManage || user?.role === "supervisor") && (
              <Button variant="outline" size="sm" onClick={() => window.open("/kiosk-signatures", "_blank")}
                      data-testid="hr-open-kiosk"
                      className="gap-1.5 border-slate-300 text-slate-800 hover:bg-slate-100">
                <UsersFour size={14} weight="bold" /> Modo Kiosco
              </Button>
            )}
            {(canManage || user?.role === "supervisor") && (
              <Button variant="outline" size="sm" data-testid="hr-remind-signatures"
                      className="gap-1.5 border-slate-300 text-slate-800 hover:bg-slate-100"
                      onClick={async () => {
                        try {
                          const { data } = await api.post("/hr/reminders/pending-signatures");
                          if (data.pending_total === 0) {
                            toast.success("✔ Todos firmaron su asistencia hoy");
                          } else {
                            toast.success(`⏰ ${data.notified} recordatorio(s) enviados · ${data.pending_total} pendientes`);
                          }
                        } catch (e) { toast.error(apiError(e)); }
                      }}>
                <ClockCounterClockwise size={14} weight="bold" /> Recordar firmas
              </Button>
            )}
            {(canManage || user?.role === "supervisor") && (
              <Button size="sm" data-testid="hr-open-twilio"
                      onClick={() => setTab("whatsapp")}
                      className="gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white shadow-md">
                <WhatsappLogo size={14} weight="fill" /> Configurar WhatsApp / Twilio
              </Button>
            )}
            <CareersQrDialog open={qrOpen} onOpenChange={setQrOpen} />
          </>
        } />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 flex-wrap h-auto gap-0 p-1 justify-start">
          {TAB_GROUPS.map((grp, gi) => (
            <div key={grp.cat} className="flex items-center gap-1 pl-1 pr-2 py-0.5"
                  data-testid={`hr-tab-group-${grp.cat.toLowerCase()}`}>
              <span className="text-[9px] uppercase tracking-widest font-bold text-slate-400 mr-1">
                {grp.cat}
              </span>
              {grp.tabs.map((tabCfg) => (
                <TabsTrigger
                  key={tabCfg.v}
                  value={tabCfg.v}
                  data-testid={`hr-tab-${tabCfg.v}`}
                  className="text-slate-600 font-semibold data-[state=active]:bg-white data-[state=active]:text-indigo-900 data-[state=active]:shadow-sm hover:text-slate-900 hover:bg-white/60 transition gap-1.5"
                >
                  <tabCfg.I size={14} weight="duotone" /> {t(tabCfg.l)}
                </TabsTrigger>
              ))}
              {gi < TAB_GROUPS.length - 1 && (
                <span className="w-px h-6 bg-slate-300/60 mx-1" aria-hidden="true" />
              )}
            </div>
          ))}
        </TabsList>

        <TabsContent value="dashboard" className="mt-4"><KpisTab canManage={canManage} /></TabsContent>
        <TabsContent value="employees" className="mt-4"><EmployeesTab canManage={canManage} /></TabsContent>
        <TabsContent value="schedule" className="mt-4"><CalendarTab canManage={canManage} /></TabsContent>
        <TabsContent value="attendance" className="mt-4"><AttendanceTab canManage={canManage || user?.role === "supervisor"} /></TabsContent>
        <TabsContent value="requests" className="mt-4"><HrRequestsTab canManage={canManage} /></TabsContent>
        <TabsContent value="certs" className="mt-4"><CertificationsTab /></TabsContent>
        <TabsContent value="orgchart" className="mt-4"><OrgChartTab /></TabsContent>
        <TabsContent value="vacancies" className="mt-4"><VacanciesTab canManage={canManage} /></TabsContent>
        <TabsContent value="talent" className="mt-4"><TalentPoolTab canManage={canManage} /></TabsContent>
        <TabsContent value="reimb" className="mt-4"><ReimbursementsTab canManage={canManage || user?.role === "supervisor"} /></TabsContent>
        <TabsContent value="signatures" className="mt-4"><SignaturesLogTab /></TabsContent>
        <TabsContent value="whatsapp" className="mt-4 space-y-6">
          <TwilioDiagnosticsPanel />
          <WhatsAppLogTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pre-nómina · reporte por período con incidencias + devengado + descuentos
// ---------------------------------------------------------------------------
function PrePayrollButton() {
  const [open, setOpen] = useState(false);
  const today = new Date();
  const startMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const iso = (d) => d.toISOString().slice(0, 10);
  const [range, setRange] = useState({ date_from: iso(startMonth), date_to: iso(today) });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signatures, setSignatures] = useState({});  // {employee_id → doc}
  const [signing, setSigning] = useState(null);  // {emp, sig?}

  const load = async (r = range) => {
    setBusy(true);
    try {
      const res = await api.get("/hr/pre-payroll", { params: r });
      setData(res.data);
      const sig = await api.get("/hr/pre-payroll/signatures", { params: r });
      setSignatures(sig.data.signatures || {});
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    if (open && !data) load(range);
  }, [open]);  // load intencionalmente estable a la apertura del diálogo

  const openSign = (emp) => setSigning({ emp, sig: signatures[emp.employee_id]?.signature || "" });

  const saveSignature = async () => {
    if (!signing?.sig || !signing.sig.startsWith("data:image/")) {
      toast.error("El colaborador debe firmar antes de guardar"); return;
    }
    try {
      await api.post("/hr/pre-payroll/signatures", {
        employee_id: signing.emp.employee_id,
        date_from: range.date_from, date_to: range.date_to,
        signature: signing.sig,
        signed_by_name: signing.emp.name,
      });
      toast.success(`Firma de ${signing.emp.name} guardada`);
      setSigning(null);
      const sig = await api.get("/hr/pre-payroll/signatures", { params: range });
      setSignatures(sig.data.signatures || {});
    } catch (e) { toast.error(apiError(e)); }
  };

  const clearSignature = async (emp) => {
    if (!window.confirm(`¿Borrar la firma de ${emp.name}?`)) return;
    try {
      await api.delete(`/hr/pre-payroll/signatures/${emp.employee_id}`, { params: range });
      toast.success("Firma borrada");
      const sig = await api.get("/hr/pre-payroll/signatures", { params: range });
      setSignatures(sig.data.signatures || {});
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadXlsx = async () => {
    try {
      const r = await api.get("/hr/pre-payroll/xlsx", { params: range, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `pre-nomina-${range.date_from}-a-${range.date_to}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get("/hr/pre-payroll/pdf", { params: range, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `pre-nomina-${range.date_from}-a-${range.date_to}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Pre-nómina PDF descargada");
    } catch (e) { toast.error(apiError(e)); }
  };

  const t = data?.totals || {};
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1" data-testid="hr-pre-payroll-btn">
          <Receipt size={14} weight="bold" /> Pre-nómina
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="hr-pre-payroll-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Receipt size={20} weight="fill" className="text-blue-900" />
            Pre-nómina · reporte por período
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3 border-b border-slate-200 pb-3">
          <div><Label className="overline-label">Desde</Label>
            <Input type="date" data-testid="pre-payroll-from" value={range.date_from}
              onChange={(e) => setRange({ ...range, date_from: e.target.value })} /></div>
          <div><Label className="overline-label">Hasta</Label>
            <Input type="date" data-testid="pre-payroll-to" value={range.date_to}
              onChange={(e) => setRange({ ...range, date_to: e.target.value })} /></div>
          <Button data-testid="pre-payroll-apply" onClick={() => load()}
            disabled={busy} className="bg-blue-900 hover:bg-blue-800">
            {busy ? "Cargando…" : "Actualizar"}
          </Button>
          <Button data-testid="pre-payroll-xlsx" variant="outline" onClick={downloadXlsx}
            disabled={!data} className="gap-1 ml-auto">
            <DownloadSimple size={14} weight="bold" /> Exportar Excel
          </Button>
          <Button data-testid="pre-payroll-pdf" onClick={downloadPdf}
            disabled={!data} className="gap-1 bg-blue-900 hover:bg-blue-800">
            <FilePdf size={14} weight="bold" /> PDF Ejecutivo
          </Button>
        </div>

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 my-3">
              {[
                { label: "Devengado", v: t.devengado_est, tone: "text-emerald-700" },
                { label: "Pago extras", v: t.pago_extras, tone: "text-blue-800", subtitle: `${t.dias_extra ?? 0} días extra (día doble)` },
                { label: "Destajo", v: t.destajo_neto, tone: "text-fuchsia-700", subtitle: `Bruto ${(t.destajo_bruto ?? 0).toLocaleString()} · Desc. ${(t.destajo_descuentos ?? 0).toLocaleString()}` },
                { label: "Descuentos", v: t.descuentos, tone: "text-red-600" },
                { label: "Reembolsos", v: t.reembolsos, tone: "text-indigo-700" },
                { label: "Neto est.", v: t.neto_est, tone: "text-slate-900" },
              ].map((k) => (
                <div key={k.label} className="rounded-md border border-slate-200 bg-white p-3">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{k.label}</p>
                  <p className={`font-heading font-black text-2xl mt-1 tabular-nums ${k.tone}`}>
                    {(k.v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                  {k.subtitle && <p className="text-[9px] text-slate-500 mt-0.5">{k.subtitle}</p>}
                </div>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full data-table text-xs" data-testid="pre-payroll-table">
                <thead className="sticky top-0 bg-white z-10">
                  <tr>
                    <th>DNI</th><th>Nombre</th><th>Puesto</th><th>Área</th>
                    <th className="text-center" title="Firma digital del colaborador">Firma</th>
                    <th className="text-right">Base</th><th className="text-right">Jornal</th>
                    <th className="text-center" title="Presente">P</th>
                    <th className="text-center text-red-600" title="Ausente">A</th>
                    <th className="text-center" title="Justificado">J</th>
                    <th className="text-center" title="Vacaciones">V</th>
                    <th className="text-center" title="Permiso">Pm</th>
                    <th className="text-center" title="Médico">M</th>
                    <th className="text-center" title="Descanso">D</th>
                    <th className="text-center text-blue-800" title="Días extra (trabajó en día de descanso)">Ext</th>
                    <th className="text-right">Devengado</th>
                    <th className="text-right text-blue-800" title="Pago por días extra al doble">Extras</th>
                    <th className="text-right text-fuchsia-700" title="Destajo bruto (piecework por operaciones)">Destajo br.</th>
                    <th className="text-right text-rose-600" title="Descuentos por cláusulas del auditor sobre el destajo">Cláus.</th>
                    <th className="text-right text-fuchsia-800" title="Destajo neto (lo que efectivamente se paga)">Destajo neto</th>
                    <th className="text-right text-red-600">Descuento</th>
                    <th className="text-right">Reemb.</th>
                    <th className="text-right">Neto</th>
                  </tr>
                </thead>
                <tbody>
                  {data.employees.map((r) => (
                    <tr key={r.employee_id} data-testid={`pre-payroll-row-${r.dni}`}>
                      <td className="font-mono">{r.dni}</td>
                      <td className="font-medium">{r.name}</td>
                      <td className="text-slate-600">{r.position}</td>
                      <td className="text-slate-500">{r.area}</td>
                      <td className="text-center">
                        {signatures[r.employee_id] ? (
                          <div className="flex items-center justify-center gap-1"
                               data-testid={`pre-payroll-signed-${r.dni}`}>
                            <img src={signatures[r.employee_id].signature} alt="firma"
                                 className="h-6 w-16 object-contain border border-emerald-300 rounded bg-emerald-50 cursor-pointer"
                                 onClick={() => openSign(r)} title="Ver / actualizar firma" />
                            <button type="button" onClick={() => clearSignature(r)}
                                    className="text-red-400 hover:text-red-600 text-[10px]"
                                    title="Borrar firma">✕</button>
                          </div>
                        ) : (
                          <button type="button" onClick={() => openSign(r)}
                                  data-testid={`pre-payroll-sign-${r.dni}`}
                                  className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-900 text-white hover:bg-blue-700">
                            ✍ Firmar
                          </button>
                        )}
                      </td>
                      <td className="text-right font-mono">{r.base_salary.toLocaleString()}</td>
                      <td className="text-right font-mono">{r.jornal_diario.toLocaleString()}</td>
                      <td className="text-center font-mono">{r.days_present}</td>
                      <td className="text-center font-mono text-red-600">{r.days_absent}</td>
                      <td className="text-center font-mono">{r.days_justified}</td>
                      <td className="text-center font-mono">{r.days_vacation}</td>
                      <td className="text-center font-mono">{r.days_permit}</td>
                      <td className="text-center font-mono">{r.days_medical}</td>
                      <td className="text-center font-mono">{r.days_rest}</td>
                      <td className={`text-center font-mono font-bold ${r.dias_extra > 0 ? "text-blue-800" : "text-slate-300"}`}
                          data-testid={`pre-payroll-extras-${r.dni}`}>{r.dias_extra ?? 0}</td>
                      <td className="text-right font-mono font-bold text-emerald-700">
                        {r.devengado_est.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-right font-mono text-blue-800">
                        {(r.pago_extras ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className={`text-right font-mono ${(r.destajo_bruto ?? 0) > 0 ? "text-fuchsia-700 font-bold" : "text-slate-300"}`}
                          data-testid={`pre-payroll-destajo-bruto-${r.dni}`}
                          title={(r.destajo_conceptos || []).map((c) => `${c.label}: ${c.qty} ${c.unit} = $${(c.amount || 0).toLocaleString()}`).join("\n")}>
                        {(r.destajo_bruto ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className={`text-right font-mono ${(r.destajo_descuentos ?? 0) > 0 ? "text-rose-600" : "text-slate-300"}`}
                          data-testid={`pre-payroll-destajo-desc-${r.dni}`}
                          title={(r.destajo_clausulas || []).join(", ")}>
                        {(r.destajo_descuentos ?? 0) > 0 ? `-${(r.destajo_descuentos ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "-"}
                      </td>
                      <td className={`text-right font-mono ${(r.destajo_neto ?? 0) > 0 ? "text-fuchsia-800 font-bold" : "text-slate-300"}`}
                          data-testid={`pre-payroll-destajo-neto-${r.dni}`}>
                        {(r.destajo_neto ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-right font-mono text-red-600">
                        {r.descuentos.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-right font-mono text-indigo-700">
                        {r.reembolsos.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-right font-mono font-bold">
                        {r.neto_est.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                  {data.employees.length === 0 && (
                    <tr><td colSpan={23} className="text-center text-slate-400 py-6">Sin empleados en el período.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-slate-400 mt-2 font-mono">
              Jornal = base_salary / 30 · Devengado = (P + J + V) × jornal + Extras · Descuento = A × jornal ·
              <b> Día extra</b> = presente en día que el rol marcaba descanso; se paga al doble (LFT art. 73) →
              Extras = <b>Ext × jornal</b> (el 2do jornal; el 1º ya cuenta como P). ·
              <b> Destajo</b> = suma de conceptos (barrenación, anclaje, enmallado…) del período <b>menos</b>
              cláusulas del auditor (avance_bajo_85, afines_desbordes, daños). Hover sobre &ldquo;Destajo br.&rdquo; muestra el detalle. ·
              Neto = Devengado − Descuento + Reembolsos <b>+ Destajo neto</b>. Cifras <b>estimadas</b>, la nómina definitiva la cierra contabilidad.
            </p>
          </>
        )}
      </DialogContent>

      {/* -------- Dialog Firma del colaborador (modo kiosko) -------- */}
      <Dialog open={!!signing} onOpenChange={(v) => !v && setSigning(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="pre-payroll-sign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              ✍ Firma del colaborador · modo kiosko
            </DialogTitle>
          </DialogHeader>
          {signing && (
            <div className="space-y-3">
              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <p className="text-[10px] uppercase font-bold text-slate-500">Recibí conforme</p>
                <p className="font-heading font-black text-lg">{signing.emp.name}</p>
                <p className="text-xs font-mono text-slate-500">
                  DNI {signing.emp.dni} · {signing.emp.position || "—"} · Período {range.date_from} → {range.date_to}
                </p>
                <p className="text-sm font-bold text-emerald-700 mt-1">
                  Neto estimado: ${(signing.emp.neto_est || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} MXN
                </p>
              </div>
              <p className="text-[11px] text-slate-600">
                Al firmar, el colaborador confirma que revisó su pre-nómina y está de acuerdo con las cifras
                mostradas. Esta firma se incrustará en el PDF ejecutivo con marca de tiempo y IP para auditoría.
              </p>
              <div>
                <Label className="overline-label">Firma digital</Label>
                <SignaturePad
                  value={signing.sig || ""}
                  onChange={(sig) => setSigning({ ...signing, sig })}
                  width={430} height={140}
                  testid="pre-payroll-sign-pad" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSigning(null)}>Cancelar</Button>
            <Button onClick={saveSignature} className="bg-emerald-700 hover:bg-emerald-600"
                    data-testid="pre-payroll-sign-save">
              Confirmar firma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
